import React, { useState, useEffect, useRef } from 'react';
import {
  Wallet, Activity, TrendingUp, CalendarClock, Users,
  Target, Scale, TriangleAlert,
} from 'lucide-react';
import api from '../../services/api';
import { getSocket } from '../../services/socket';
import { useAccounts } from '../../context/AccountContext';
import TradeTable from '../../components/TradeTable';
import KPITile from '../../components/KPITile';

const money = n => `${n >= 0 ? '+' : '−'}$${Math.abs(Number(n || 0)).toFixed(2)}`;
const plain = n => `$${Number(n || 0).toFixed(2)}`;
const intentOf = n => (n > 0 ? 'profit' : n < 0 ? 'loss' : 'neutral');
const trendOf  = n => (n > 0 ? 'up' : n < 0 ? 'down' : 'flat');

export default function TraderOverview() {
  const { scopeParams, selectedId } = useAccounts();
  const [dash, setDash]       = useState(null);
  const [trades, setTrades]   = useState([]);
  const [loading, setLoading] = useState(true);
  // See the copier Overview: one account's tick is not the portfolio's
  // balance, so a combined view refetches (throttled) instead of merging.
  const lastDashFetch = useRef(0);

  useEffect(() => {
    const req = { params: scopeParams };
    setLoading(true);
    Promise.all([
      api.get('/trader/dashboard', req),
      api.get('/trader/trades/open', req),
    ]).then(([d, t]) => {
      setDash(d.data);
      setTrades(t.data);
      lastDashFetch.current = Date.now();
    }).catch(() => {}).finally(() => setLoading(false));

    const sock = getSocket();
    if (!sock) return undefined;
    const reloadDash = () => {
      if (Date.now() - lastDashFetch.current < 10000) return;
      lastDashFetch.current = Date.now();
      api.get('/trader/dashboard', req).then(r => setDash(r.data)).catch(() => {});
    };
    const onAccount = (data) => {
      if (selectedId) {
        if (data.accountId && data.accountId !== selectedId) return;
        setDash(prev => prev ? { ...prev, balance: data.balance, equity: data.equity } : prev);
        return;
      }
      reloadDash();
    };
    const reloadTrades = () =>
      api.get('/trader/trades/open', req).then(r => setTrades(r.data)).catch(() => {});
    sock.on('account:update', onAccount);
    sock.on('trade:opened', reloadTrades);
    sock.on('trade:closed', reloadTrades);
    return () => {
      sock.off('account:update', onAccount);
      sock.off('trade:opened', reloadTrades);
      sock.off('trade:closed', reloadTrades);
    };
  }, [scopeParams, selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <p style={{ color: 'var(--text-secondary)' }}>Loading dashboard…</p>;

  const openPnl  = dash?.openPnl  ?? 0;
  // false when any open position had no fresh live reading (see
  // backend/services/live-positions.js) — don't present it as current.
  const openPnlSub = dash?.openPnlLive === false
    ? 'Floating — some readings delayed'
    : (dash?.openPnl ?? 0) >= 0 ? 'Floating profit' : 'Floating loss';
  const todayPnl = dash?.todayPnl ?? 0;

  return (
    <>
      <div className="ct-page-header">
        <div>
          <span className="eyebrow">Trader</span>
          <h1>Overview</h1>
          <div className="subtitle">Your live account, performance, and open positions</div>
        </div>
      </div>

      <div className="row">
        <Tile><KPITile label="Balance" value={plain(dash?.balance)} sub="Account balance"
          icon={<Wallet size={16} />} intent="neutral" /></Tile>
        <Tile><KPITile label="Equity" value={plain(dash?.equity)} sub="Incl. floating P&L"
          icon={<Activity size={16} />} intent="neutral" /></Tile>
        <Tile><KPITile label="Open P&L" value={money(openPnl)} sub={openPnlSub}
          icon={<TrendingUp size={16} />} intent={intentOf(openPnl)} trend={trendOf(openPnl)} /></Tile>
        <Tile><KPITile label="Today P&L" value={money(todayPnl)} sub={todayPnl >= 0 ? 'In profit' : 'In loss'}
          icon={<CalendarClock size={16} />} intent={intentOf(todayPnl)} trend={trendOf(todayPnl)} /></Tile>

        <Tile><KPITile label="Followers" value={dash?.followers ?? 0} sub="Copiers mirroring you"
          icon={<Users size={16} />} intent="neutral" /></Tile>
        <Tile><KPITile label="Win Rate" value={`${dash?.winRate ?? 0}%`} sub={`${dash?.totalTrades ?? 0} total trades`}
          icon={<Target size={16} />} intent="neutral" /></Tile>
        <Tile><KPITile label="Profit Factor" value={dash?.profitFactor ?? 0} sub="Gross profit ÷ loss"
          icon={<Scale size={16} />} intent="neutral" /></Tile>
        <Tile><KPITile label="Max Drawdown" value={`${dash?.maxDrawdown ?? 0}%`} sub="Peak-to-trough"
          icon={<TriangleAlert size={16} />} intent="warning" /></Tile>
      </div>

      <div className="row">
        <div className="col-12 mb-4">
          <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h5 className="card-title">Open Positions</h5>
                <div className="card-category" style={{ marginTop: 4 }}>{trades.length} live</div>
              </div>
              <Activity size={16} color="var(--text-tertiary)" />
            </div>
            <div className="card-body">
              <TradeTable trades={trades} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const Tile = ({ children }) => <div className="col-lg-3 col-md-6 col-6 mb-4">{children}</div>;
